import type { Organization } from '@beacon/core';
import type {
  OrganizationCreateInput,
  OrganizationDomainAddInput,
  OrganizationUpdateInput,
} from '@beacon/core/validation';

import type { BeaconClient } from '../client.js';

export interface OrganizationListResponse {
  organizations: Organization[];
}

export interface OrganizationDetail {
  id: string;
  name: string;
  type: 'client' | 'internal';
  timezone: string;
  logoUrl: string | null;
  primaryContactName: string | null;
  primaryContactEmail: string | null;
  primaryContactPhone: string | null;
  createdAt: string;
  domains: Array<{ id: string; domain: string }>;
}

export const organizationsApi = {
  list(client: BeaconClient): Promise<OrganizationListResponse> {
    return client.get<OrganizationListResponse>('/api/organizations');
  },

  getById(client: BeaconClient, id: string): Promise<OrganizationDetail> {
    return client.get<OrganizationDetail>(
      `/api/organizations/${encodeURIComponent(id)}`
    );
  },

  create(
    client: BeaconClient,
    input: OrganizationCreateInput
  ): Promise<{ id: string; name: string; type: 'client' | 'internal'; domains: string[] }> {
    return client.post('/api/organizations', input);
  },

  update(
    client: BeaconClient,
    id: string,
    input: OrganizationUpdateInput
  ): Promise<OrganizationDetail> {
    return client.patch<OrganizationDetail>(
      `/api/organizations/${encodeURIComponent(id)}`,
      input
    );
  },

  addDomain(
    client: BeaconClient,
    id: string,
    input: OrganizationDomainAddInput
  ): Promise<{ id: string; domain: string }> {
    return client.post(
      `/api/organizations/${encodeURIComponent(id)}/domains`,
      input
    );
  },

  removeDomain(
    client: BeaconClient,
    id: string,
    domainId: string
  ): Promise<void> {
    return client.delete<void>(
      `/api/organizations/${encodeURIComponent(id)}/domains/${encodeURIComponent(domainId)}`
    );
  },
};
